<template>
  <!-- Tabs are gone now that we unified the nav
  <div
    class="top-level-tabs"
    :style="['mobile', 'tablet', 'tablet-large'].includes(screenSize) ? 'margin: 0 -26px 30px' : 'margin: -23px -26px 40px'"
  >
    <div
      :class="{
        'top-level-tabs__tab': true,
        'top-level-tabs__tab--inactive': activeTopLevelTab !== tabName,
      }"
      v-for="tabName in Object.values(TopLevelTabNames)"
      :key="tabName"
      @click="activeTopLevelTab = tabName"
    >
      <div
        :class="{
          'tab-icon-and-text': true,
          'tab-icon-and-text--inactive': activeTopLevelTab !== tabName,
          'top-level-tabs__tab-icon-and-text': true,
        }"
      >
        <IconEmbedded v-if="tabName === TopLevelTabNames.CreatorSuite" class="tab-icon-and-text__icon" name="briefcase_1-5" :size="34" color="rgba(0, 0, 0, 0.5)" />
        <IconEmbedded v-if="tabName === TopLevelTabNames.AtlasSocial" class="tab-icon-and-text__icon" name="globe_1-5" :size="34" color="rgba(0, 0, 0, 0.5)" />

        <div class="global-h4 tab-icon-and-text__text">
          {{ tabName }}
        </div>
      </div>
    </div>
  </div>
-->

  <MenuLinksList :linksList="communityIdInContext ? navLinksAdmin : []" v-bind="$attrs" />
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import { useUserProfileStore } from "@stores/userProfileStore";
import { mapState } from "pinia";
import FeatureFlags from "@logic/FeatureFlags";
import FeatureFlagCache from "@logic/FeatureFlagCache";
import Logger from "@helpers/Logger";

// Types
import { NavigationMenuItem } from "@contracts/navigationMenuItem";
import { ScreenSize } from "@contracts/screenSize";

// Components
import MenuLinksList from "@components/Navigation/MenuLinksList.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";

export default defineComponent({
  name: "CustomerAdminLeftNav",

  components: {
    IconEmbedded,
    MenuLinksList,
  },

  props: {
    communityIdInContext: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      screenSize: inject("screenSize") as ScreenSize,

      currentRouteName: "",

      FeatureFlags,
      serverFlagEnableAtlas: false,
    };
  },

  computed: {
    ...mapState(useUserProfileStore, ["getViewingUserProfile"]),
    orgInContext() {
      return this.globalRoot.orgInContext;
    },

    navLinksAdmin(): Array<NavigationMenuItem> {
      const isSpotlight = this.orgInContext?.isSpotlight;
      const isDisabled = this.orgInContext?.isDisabled;
      const isCustomCollabOnly = this.orgInContext?.subType === "CustomCollab";

      // Dashboard & Content Library (always shown) ============================
      let navLinks: Array<NavigationMenuItem> = [
        {
          text: "Dashboard",
          isSubItem: false,
          titleIcon: { name: "dashboard_2", size: 23 },
          to: { name: "CommunityAdminDashboard", params: { communityId: this.communityIdInContext } },
        },
      ];

      // Atlas Social =======================================================
      const isAtlasEnabled = this.serverFlagEnableAtlas || (this.communityIdInContext === "funtown" && FeatureFlags.isFlagActive("EnableAtlasForFuntown"));
      if (!isDisabled && !isCustomCollabOnly) {
        navLinks.push(
          {
            text: "Atlas Social",
            isSubItem: false,
            titleIcon: { name: "globe_2", size: 23 },
            to: { name: isAtlasEnabled ? "CalendarPosts" : "AtlasOptInLanding", params: { customerId: this.communityIdInContext } },
            // isWithBetaBadge: true,
            // badgeText: "beta",
          },
          {
            text: "Content Calendar",
            isSubItem: true,
            titleIcon: { name: "bulb_2", size: 26 },
            to: { name: isAtlasEnabled ? "CalendarPosts" : "AtlasOptInLanding", params: { customerId: this.communityIdInContext } },
          }
        );
        if (isAtlasEnabled) {
          navLinks.push({
            text: "Configuration",
            isSubItem: true,
            titleIcon: { name: "bulb_2", size: 26 },
            to: { name: "CalendarConfig", params: { customerId: this.communityIdInContext } },
          });
        }
      }

      // Creator Suite ======================================================
      if (!isDisabled && !isCustomCollabOnly && !isSpotlight) {
        // Removing this from the left nav for now, except for the one customer that's actively using it.
        const showCustomCollabs = this.communityIdInContext === "keweenawmi";
        navLinks.push(
          {
            text: "Creator Suite",
            isSubItem: false,
            titleIcon: { name: "briefcase_2", size: 22 },
            to: { name: "PaidCollabCommunityAdmin", params: { communityId: this.communityIdInContext } },
          },
          {
            text: "Collaboration Hub",
            isBold: true,
            isSubItem: true,
            titleIcon: { name: "bulb_2", size: 26 },
            to: { name: "PaidCollabCommunityAdmin", params: { communityId: this.communityIdInContext } },
            isHighlightingOff: true,
          },
          {
            text: "Creator Visits",
            isSubItem: true,
            titleIcon: { name: "bulb_2", size: 26 },
            to: { name: "PaidCollabCommunityAdmin", params: { communityId: this.communityIdInContext } },
            isHidden: isDisabled || isSpotlight || isCustomCollabOnly,
          },
          /* {
            text: "Visit Inspiration",
            isSubItem: true,
            isSubSubItem: true,
            titleIcon: { name: "bulb_2", size: 26 },
            to: { name: "VisitInspiration", params: { pageId: this.communityIdInContext } },
          },
          {
            text: "Visit Examples",
            isSubItem: true,
            isSubSubItem: true,
            titleIcon: { name: "bulb_2", size: 26 },
            to: { name: "IndustryInsights", params: { pageId: this.communityIdInContext } },
          },*/
          {
            text: "Saved Creators",
            isSubItem: true,
            titleIcon: { name: "bulb_2", size: 26 },
            to: { name: "CustomerSavedCreators", params: { pageId: this.communityIdInContext } },
            isHidden: isDisabled,
          },
          {
            text: "Community Uploads",
            isSubItem: true,
            titleIcon: { name: "bulb_2", size: 26 },
            to: { name: "UserUploadCampaigns", params: { pageId: this.communityIdInContext } },
          },
          {
            text: "Custom Collabs",
            isWithBetaBadge: true,
            isSubItem: true,
            titleIcon: { name: "bulb_2", size: 26 },
            to: { name: "CustomerCustomCollabs", params: { communityId: this.communityIdInContext } },
            isHidden: isDisabled || isSpotlight || !showCustomCollabs,
          },
          {
            text: "Create an Adventure",
            isSubItem: true,
            titleIcon: { name: "bulb_2", size: 26 },
            to: { name: "MyItineraries", params: { creatorId: this.getViewingUserProfile?.sherpaId } },
            isHidden: isDisabled || this.orgInContext?.enableCreateAdventures !== true,
          },
          {
            text: "Content Launchpad",
            isBold: true,
            isSubItem: true,
            titleIcon: { name: "bulb_2", size: 26 },
            to: { name: "WidgetDirections", params: { pageId: this.communityIdInContext } },
            isHighlightingOff: true,
          },
          /* {
            text: "Website Enhancements",
            isSubItem: true,
            titleIcon: { name: "bulb_2", size: 26 },
            to: { name: "WidgetDirections", params: { pageId: this.communityIdInContext } },
            isHighlightingOff: true,
          },*/
          {
            text: "Photo Wall",
            isSubItem: true,
            // isSubSubItem: true,
            titleIcon: { name: "bulb_2", size: 26 },
            to: { name: "WidgetDirections", params: { pageId: this.communityIdInContext } },
          },
          {
            text: "Adventure Wall",
            isSubItem: true,
            // isSubSubItem: true,
            titleIcon: { name: "bulb_2", size: 26 },
            to: { name: "AdventureWidgets", params: { pageId: this.communityIdInContext } },
          },
          {
            text: "Adventure Posts",
            isSubItem: true,
            // isSubSubItem: true,
            titleIcon: { name: "bulb_2", size: 26 },
            to: { name: "AdventureSummary", params: { pageId: this.communityIdInContext } },
          }
        );
      }
      // Moved Content Library to the bottom
      navLinks.push({
        text: "Content Library",
        isSubItem: false,
        titleIcon: { name: "image_2", size: 24 },
        to: { name: "CommunityPhotos", params: { pageId: this.communityIdInContext } },
      });

      return navLinks;
    },
  },

  watch: {
    $route(_to, _from) {
      this.setRouteName();
      // @ts-ignore
      Logger.info(this.currentRouteName);
    },
    communityIdInContext: {
      immediate: true,
      handler: async function () {
        if (this.communityIdInContext) {
          await this.refreshServerFlagForTabs();
        }
      },
    },
  },

  async mounted() {
    this.setRouteName();
  },

  methods: {
    setRouteName() {
      this.currentRouteName = this.$router.currentRoute.value.name?.toString();
    },
    async refreshServerFlagForTabs() {
      try {
        this.serverFlagEnableAtlas = await FeatureFlagCache.GetFeature("social-calendar");
      } catch (e) {
        console.error(e);
        this.serverFlagEnableAtlas = false;
      }
    },
    isActive(name, aliases = []): boolean {
      return name === this.currentRouteName || aliases.includes(this.currentRouteName);
    },
  },
});
</script>

<style scoped lang="scss">
// Tab icon and text ==========================================================
.tab-icon-and-text {
  gap: 5px;
  display: flex;
  align-items: center;

  &--inactive {
    opacity: 0.3;
    text-decoration: underline;
    text-decoration-color: rgba(0, 0, 0, 0.45);
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;
    text-decoration-style: dashed;
  }

  &--inactive:hover {
    text-decoration: none;
  }

  &__text {
    line-height: 1.05;
    white-space: pre;
  }
}

// Top level tabs =============================================================
.top-level-tabs {
  height: 80px;
  display: flex;

  &__tab {
    width: 50%;
    display: flex;
    justify-content: center;
    align-content: center;
    user-select: none;

    &--inactive {
      border-radius: 0 0 6px 0;
      position: relative;
      z-index: 0;
      background: rgba(255, 255, 255, 1);
      cursor: pointer;

      &::before {
        content: "";
        width: 6px;
        height: 6px;
        position: absolute;
        inset: 0 -6px auto auto;
        background: white;
        pointer-events: none;
      }
      &::after {
        content: "";
        width: 12px;
        height: 12px;
        border-radius: 1000px;
        position: absolute;
        inset: 0 -12px auto auto;
        background: rgba(231, 237, 240, 1);
        pointer-events: none;
      }
    }
  }

  &__tab + &__tab--inactive {
    border-radius: 0 0 0 6px;

    &::before {
      inset: 0 auto auto -6px;
    }
    &::after {
      inset: 0 auto auto -12px;
    }
  }
}
</style>
